/** Auth context, backed by PocketBase's authStore (which persists to localStorage). */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import type { UserRecord } from '@/domain/types'
import { currentUser, logout as pbLogout, pb } from './pb'

interface AuthContextValue {
  user: UserRecord | null
  isLoggedIn: boolean
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserRecord | null>(() => currentUser())

  useEffect(() => {
    // Fires on login, logout, token refresh, and — because the store is backed
    // by localStorage — on changes made in another tab.
    return pb.authStore.onChange(() => setUser(currentUser()))
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoggedIn: user !== null,
      logout: () => pbLogout(),
    }),
    [user],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
